import { NotificationSettingsContainer } from '@/modules/notification-settings/containers/notification-settings-container';
import { BrowserSignoutContainer } from '@/modules/browser-auth/containers/browser-signout-container';
import { GroundDiagnosticsContainer } from '@/modules/world-map/containers/ground-diagnostics-container';
import { lazy, Suspense, type CSSProperties } from 'react';
import { GamePhase } from '@/game/types';
import { CharacterPanel } from '@/modules/hero-character/components/character-panel';
import { BiographyPanel } from '@/modules/hero-biography/components/biography-panel';
import { BiographyNotice } from '@/modules/hero-biography/components/biography-notice';
import { useHeroBiography } from '@/modules/hero-biography/hooks/use-hero-biography';
import { EncounterOverlay } from '@/modules/hero-character/components/encounter-overlay';
import { useGameSessionContext } from '@/modules/game-session/game-session-context';
import { HUD } from '@/ui/HUD';
import { CombatOverlay } from '@/modules/combat-hud/components/CombatOverlay';
import { CombatLogPanel } from '@/modules/combat-hud/components/CombatLogPanel';
import { DeathScreen } from '@/modules/combat-hud/components/DeathScreen';
import { GameToast } from '@/ui/GameToast';
import { Minimap } from '@/modules/world-map/components/Minimap';
import { GameCameraControls } from '@/modules/world-map/containers/game-camera-controls';
import { NPCInteraction } from '@/modules/town-npc/components/NPCInteraction';
import { WanderingMerchantDialog } from '@/modules/wandering-merchant/components/wandering-merchant-dialog';
import { WildEncounterPopup } from '@/ui/WildEncounterPopup';
import { HeroMeetPanel } from '@/ui/HeroMeetPanel';
import { GameIcon } from '@/shared/ui/game-icon';
import { metalPanel, copperButton, gameColors, sectionHeading } from '@/shared/ui/game-theme';

const HeroSheetModal = lazy(() =>
  import('@/modules/hero-sheet/components/HeroSheetModal').then((mod) => ({
    default: mod.HeroSheetModal,
  })),
);
const ChangelogModal = lazy(() =>
  import('@/modules/onboarding/components/ChangelogModal').then((mod) => ({
    default: mod.ChangelogModal,
  })),
);
const NameEntryScreen = lazy(() =>
  import('@/modules/onboarding/components/NameEntryScreen').then((mod) => ({
    default: mod.NameEntryScreen,
  })),
);
const OfflineReport = lazy(() =>
  import('@/modules/progression/components/OfflineReport').then((mod) => ({
    default: mod.OfflineReport,
  })),
);
const LazyNpcDialogContainer = lazy(() =>
  import('@/modules/town-npc/containers/npc-dialog-container').then((mod) => ({
    default: mod.NpcDialogContainer,
  })),
);

const appStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
};

const canvasContainerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
};

const connectionBanner: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  textAlign: 'center',
  padding: '4px 0',
  fontSize: 12,
  fontWeight: 600,
  color: gameColors.text,
  backgroundColor: '#573b35eb',
  zIndex: 200,
};

export function AppPage() {
  const { refs, state, actions, character, characterLanguage, translations: tr } = useGameSessionContext();
  const { canvasRef } = refs;
  const biography = useHeroBiography(state.gameState.hero?.id ?? null);

  const {
    gameState,
    combatEvents,
    wsConnected,
    wsEverConnected,
    buffCooldownEndsAt,
    connectionError,
    toast,
    merchantPurchaseErrorVersion,
    logEntries,
    offlineReport,
    needsName,
    changelogOpen,
    towns,
    roadNetwork,
    heroQuests,
    currentTown,
    heroSheetOpen,
    heroSheetInitialTab,
    activeWorldEvent,
    sheetNowMs,
    completedQuestCount,
    questClaimDisabled,
    inCombatLogMode,
    nearHeroLogLines,
    townTourInteractionNPC,
    interactionNpc,
    showNPCInteraction,
  } = state;

  return (
    <div style={appStyle} className="th-game">
      <div ref={canvasRef} style={canvasContainerStyle} />

      <HUD
        gameState={gameState}
        connected={wsConnected}
        towns={towns}
        onBuffActivate={actions.handleBuffActivate}
        buffCooldownEndsAt={buffCooldownEndsAt}
        onUsePotion={actions.handleUsePotion}
        onHeroUpdated={actions.handleNPCHeroUpdated}
        onPurchaseRefill={actions.handlePurchaseBuffRefill}
        completedQuestCount={completedQuestCount}
        onOpenHeroSheet={() => actions.openHeroSheet('stats')}
      />

      {activeWorldEvent?.kind === 'socialPass' &&
      activeWorldEvent.chatVisible ? (
        <HeroMeetPanel
          partnerName={activeWorldEvent.partnerName}
          anySideOnline={activeWorldEvent.anySideOnline}
          maxChars={activeWorldEvent.maxChars}
          onSendMessage={actions.handleHeroMeetSendMessage}
          onEndConversation={actions.handleHeroMeetEndConversation}
        />
      ) : null}

      <EncounterOverlay simulationPaused={character.snapshot?.simulationPaused ?? false} encounter={character.snapshot?.active ?? null} decision={character.decision} language={characterLanguage} interaction={{pending: character.pending, sending: character.sending, error: character.error, onAdvise: character.advise, onRetry: character.retry, onDismiss: character.dismissDecision}} />
      {!heroSheetOpen && !biography.submitted && <BiographyNotice cycle={biography.snapshot?.enabled ? biography.snapshot.cycle ?? null : null} clock={biography.clock} language={characterLanguage} onOpen={() => actions.openHeroSheet('biography')} />}

      <Suspense fallback={null}>
        {gameState.hero && (
          <HeroSheetModal
            connected={wsConnected}
            onEquipmentUpdated={actions.handleEquipmentAdviceUpdated}
            settingsExtra={<><NotificationSettingsContainer /><BrowserSignoutContainer /><GroundDiagnosticsContainer engineRef={refs.engineRef} /></>}
            characterContent={<CharacterPanel embedded profile={character.snapshot?.profile ?? null} language={characterLanguage} error={character.error} unavailable={character.unavailable} />}
            biographyContent={<BiographyPanel key={biography.snapshot?.cycle?.id ?? 'portrait'} snapshot={biography.snapshot} clock={biography.clock} language={characterLanguage} loading={biography.loading} loadError={biography.loadError} sending={biography.sending} error={biography.error} submitted={biography.submitted} onSubmit={biography.submit} onRetry={biography.refresh} />}
            open={heroSheetOpen}
            onClose={actions.closeHeroSheet}
            initialTab={heroSheetInitialTab}
            hero={gameState.hero}
            nowMs={sheetNowMs}
            equipment={gameState.hero.equipment ?? {}}
            logEntries={logEntries}
            journalLoading={character.journalLoading}
            journalFailed={character.journalFailed}
            onLoadJournal={character.loadJournal}
            quests={heroQuests}
            onQuestClaim={actions.handleQuestClaim}
            onQuestAbandon={actions.handleQuestAbandon}
            questClaimDisabled={questClaimDisabled}
          />
        )}

        {needsName && <NameEntryScreen onNameSet={actions.handleNameSet} />}

        {changelogOpen && (
          <ChangelogModal
            title={changelogOpen.payload.title}
            items={changelogOpen.payload.items}
            localized={changelogOpen.payload.localized}
            serverVersion={changelogOpen.serverVersion}
            onDismiss={actions.handleDismissChangelog}
          />
        )}

        {offlineReport && (
          <OfflineReport
            monstersKilled={offlineReport.monstersKilled}
            xpGained={offlineReport.xpGained}
            goldGained={offlineReport.goldGained}
            levelsGained={offlineReport.levelsGained}
            deaths={offlineReport.deaths}
            revives={offlineReport.revives}
            loot={offlineReport.loot}
            message={offlineReport.message}
            onDismiss={() => actions.setOfflineReport(null)}
          />
        )}

        {state.dialogNpc ? <LazyNpcDialogContainer /> : null}
      </Suspense>

      <CombatOverlay events={combatEvents} onExpire={actions.expireCombatEvent} />

      <CombatLogPanel
        visible={Boolean(gameState.hero) && nearHeroLogLines.length > 0}
        lines={nearHeroLogLines}
        anchor={gameState.enemyOnScreenRight !== false ? 'left' : 'right'}
        title={inCombatLogMode ? tr.combatLogTitle : tr.journal}
        maxVisibleLines={inCombatLogMode ? 5 : 7}
        onOpenLog={actions.openAdventureLog}
      />

      <DeathScreen
        visible={gameState.phase === GamePhase.Dead}
        onRevive={actions.handleRevive}
        manualRevivesUnlimited={gameState.hero?.manualRevivesUnlimited === true}
        subscriptionUnlimited={!!gameState.hero?.subscriptionActive}
        revivesRemaining={Math.max(0, 2 - (gameState.hero?.reviveCount ?? 0))}
      />

      {toast && (
        <GameToast message={toast.message} color={toast.color} onDone={actions.dismissToast} />
      )}

      {currentTown && (
        <div
          style={{
            position: 'absolute',
            top: 48,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '3px 14px',
            borderRadius: 6,
            backgroundColor: 'rgba(218, 165, 32, 0.15)',
            border: '1px solid rgba(218, 165, 32, 0.3)',
            fontSize: 11,
            fontWeight: 600,
            zIndex: 30,
            pointerEvents: 'none',
            ...metalPanel,
            color: gameColors.gold,
            fontFamily: 'Georgia, serif',
          }}
        >
          {currentTown.name}
        </div>
      )}

      {showNPCInteraction && interactionNpc && (
        <NPCInteraction
          npc={interactionNpc}
          onViewQuests={actions.handleNPCViewQuests}
          onOpenServiceDialog={actions.handleNPCViewQuests}
          onDismiss={actions.handleNPCInteractionDismiss}
          onDismissTownTour={
            townTourInteractionNPC ? actions.handleTownTourInteractionDismiss : undefined
          }
        />
      )}

      {activeWorldEvent?.kind === 'questAlms' && (
        <WanderingMerchantDialog
          npcName={activeWorldEvent.npcName}
          message={activeWorldEvent.message}
          phase={activeWorldEvent.phase}
          offers={activeWorldEvent.offers}
          heroGold={gameState.hero?.gold ?? 0}
          purchaseErrorVersion={merchantPurchaseErrorVersion}
          onBuySlot={actions.handleWanderingBuySlot}
          onLeave={actions.handleWanderingLeave}
        />
      )}

      {activeWorldEvent?.kind === 'wild' && (
        <WildEncounterPopup
          eventId={activeWorldEvent.eventId}
          family={activeWorldEvent.family}
          actions={activeWorldEvent.actions}
          promptEndsAt={activeWorldEvent.promptEndsAt}
          onAction={actions.handleWildAction}
        />
      )}

      {activeWorldEvent?.kind === 'roadPoi' && (
        <WildEncounterPopup
          eventId={activeWorldEvent.eventId}
          family="road_poi"
          actions={activeWorldEvent.actions}
          promptEndsAt={activeWorldEvent.promptEndsAt}
          onAction={actions.handleRoadPOIAction}
        />
      )}

      {gameState.hero && (
        <Minimap
          cameraControls={<GameCameraControls engineRef={refs.engineRef} />}
          heroX={gameState.hero.position.x}
          heroY={gameState.hero.position.y}
          towns={towns}
          routeWaypoints={gameState.routeWaypoints}
          roadNetwork={roadNetwork}
        />
      )}

      {wsEverConnected && !wsConnected && <div style={connectionBanner}>Reconnecting...</div>}

      {connectionError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.85)',
            color: '#fff',
            zIndex: 1000,
            fontFamily: 'system-ui, sans-serif',
            textAlign: 'center',
            padding: 24,
          }}
        >
          <div style={{ marginBottom: 16, color: gameColors.danger }}><GameIcon name="warning" size={38} /></div>
          <div style={{ ...sectionHeading, fontSize: 22, marginBottom: 8 }}>Connection Failed</div>
          <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 24 }}>{connectionError}</div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 28px',
              fontSize: 14,
              fontWeight: 600,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              ...copperButton,
            }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
